import { Sidebar } from '@/components/layout/Sidebar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { dataTeamServices } from '@/data/dataTeamServices';
import { useAuth } from '@/lib/auth';
import { Mail, Clock3, CheckCircle, Sparkles } from 'lucide-react';
import { useMemo } from 'react';

const contactDirectory = {
  Susan: {
    name: 'Susan Ruinaard',
    email: 'susan.ruinaard@studiobereikbaar.nl',
  },
  Roland: {
    name: 'Roland Kager',
    email: 'roland.kager@studiobereikbaar.nl',
  },
  Giovanni: {
    name: 'Giovanni Antoniazzi',
    email: 'giovanni.antoniazzi@studiobereikbaar.nl',
  },
  Younes: {
    name: 'Younes Foukalne',
    email: 'younes.foukalne@studiobereikbaar.nl',
  },
};

const palette = {
  antwerp: '#068AA1',
  antwerpTint: '#E6F6F9',
  antwerpBorder: '#9FD7E3',
  eugeniaTint: '#FCEDEE',
};

export default function DataTeamServicesPage() {
  const { user } = useAuth();

  const groupedServices = useMemo(() => {
    return dataTeamServices.reduce(
      (acc, service) => {
        if (service.status === 'available') {
          acc.available.push(service);
        } else {
          acc.comingSoon.push(service);
        }
        return acc;
      },
      { available: [] as typeof dataTeamServices, comingSoon: [] as typeof dataTeamServices },
    );
  }, []);

  const handleRequest = (email: string, title: string) => {
    const subject = encodeURIComponent(`${title} request`);
    window.location.href = `mailto:${email}?subject=${subject}`;
  };

  return (
    <div className="flex h-screen overflow-hidden" style={{ backgroundColor: palette.antwerpTint }}>
      <Sidebar activeItem="services" user={user} />

      <main id="main-content" className="flex-1 overflow-y-auto">
        <div className="mx-auto flex max-w-6xl flex-col gap-10 px-6 py-10 md:px-10">
          <header className="rounded-3xl bg-white p-8 shadow-sm border" style={{ borderColor: palette.antwerpBorder }}>
            <p className="text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: palette.antwerp }}>
              Data Team Services
            </p>
            <h1 className="mt-3 text-3xl font-bold text-sb-black">Request custom analysis</h1>
            <p className="mt-3 text-base text-sb-grey">
              These services require coordination with the SB data team. Pick the right workflow,
              share inputs, and we deliver within the stated turnaround time.
            </p>

            <div
              className="mt-6 grid gap-4 rounded-2xl border bg-[#fff] p-4 text-sm md:grid-cols-4"
              style={{ borderColor: palette.antwerpBorder, color: palette.antwerp }}
            >
              {[
                { label: '1. Review service', helper: 'Understand what we deliver' },
                { label: '2. Provide inputs', helper: 'Upload data + study scope' },
                { label: '3. Request service', helper: 'Email or form submission' },
                { label: '4. Delivery', helper: 'PDF, GeoPackage, dashboard' },
              ].map((step) => (
                <div key={step.label}>
                  <p className="font-semibold">{step.label}</p>
                  <p className="text-sm text-[#0f6b7c]/80">{step.helper}</p>
                </div>
              ))}
            </div>
          </header>

          <section>
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-semibold text-sb-black">Available services</h2>
              <p className="text-sm text-sb-dark-grey">
                Five clearly-scoped workflows with half-day to one-day turnaround. Each card spells out
                when to request it, who leads it, and what you need to provide.
              </p>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-5">
              {groupedServices.available.map((service) => {
                const contact = contactDirectory[service.contactPerson];
                return (
                  <article
                    key={service.id}
                    id={`service-${service.id}`}
                    className="rounded-3xl border bg-white p-6 shadow-sm"
                    style={{ borderColor: palette.antwerpBorder }}
                  >
                    <div className="flex flex-col gap-4 lg:flex-row">
                      <div className="overflow-hidden rounded-2xl bg-[#EBEFF2] lg:w-1/3">
                        <img
                          src={service.previewImage}
                          alt={service.title}
                          className="h-48 w-full object-cover lg:h-full"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 space-y-4">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                          <div>
                            <Badge
                              className="rounded-full"
                              style={{
                                backgroundColor: palette.antwerpTint,
                                color: palette.antwerp,
                                border: `1px solid ${palette.antwerpBorder}`,
                              }}
                            >
                              Data Team Service
                            </Badge>
                            <h3 className="mt-3 text-xl font-semibold text-sb-black">
                              {service.title}
                            </h3>
                            <p className="text-sm text-sb-dark-grey">{service.tagline}</p>
                          </div>
                          <div className="flex flex-col items-start gap-2 md:items-end">
                            <div className="flex items-center gap-2 text-sm font-medium" style={{ color: palette.antwerp }}>
                              <Clock3 size={16} />
                              {service.timeEstimate}
                            </div>
                            <Badge className="rounded-full text-white" style={{ backgroundColor: palette.antwerp }}>
                              {contact.name}
                            </Badge>
                          </div>
                        </div>

                        <p className="text-sm leading-relaxed text-sb-dark-grey">
                          {service.description}
                        </p>

                        <div className="grid gap-4 md:grid-cols-3">
                          <div className="rounded-2xl border border-[#E4E7EC] p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              When to request
                            </p>
                            <p className="mt-2 text-sm text-sb-dark-grey">
                              {service.tagline}
                            </p>
                          </div>
                          <div className="rounded-2xl border border-[#E4E7EC] p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Inputs needed
                            </p>
                            <ul className="mt-2 space-y-1 text-sm text-sb-dark-grey">
                              {service.whatYouProvide.map((item) => (
                                <li key={item} className="flex items-start gap-2">
                                  <CheckCircle size={14} className="mt-0.5" style={{ color: palette.antwerp }} />
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          <div className="rounded-2xl border border-[#E4E7EC] p-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Output format
                            </p>
                            <p className="mt-2 text-sm text-sb-dark-grey">
                              {service.id === 'multimodal-catchment'
                                ? 'Maps + GeoPackage + optional PDF'
                                : 'PDF deliverable + GeoPackage or dashboard'}
                            </p>
                            {service.relatedInnoFlashes && service.relatedInnoFlashes.length > 0 && (
                              <p className="mt-2 text-xs" style={{ color: palette.antwerp }}>
                                Related InnoFlash: #{service.relatedInnoFlashes.join(', ')}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div
                      className="mt-6 flex flex-col gap-3 rounded-2xl border bg-white/70 p-4 text-sm md:flex-row md:items-center md:justify-between"
                      style={{ borderColor: palette.antwerpBorder, color: palette.antwerp }}
                    >
                      <div>
                        <p className="font-semibold">Delivery</p>
                        <p className="text-sm" style={{ color: '#0F6B7C' }}>
                          {service.title} is delivered as PDF + GeoPackage unless otherwise noted.
                        </p>
                      </div>
                      <Button
                        className="rounded-full text-white hover:opacity-90"
                        style={{ backgroundColor: palette.antwerp }}
                        onClick={() => handleRequest(contact.email, service.title)}
                      >
                        Request Service
                        <Mail size={16} className="ml-2" />
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>

          {groupedServices.comingSoon.length > 0 && (
            <section
              className="rounded-3xl border border-dashed p-6 shadow-sm"
              style={{ borderColor: palette.eugeniaTint, backgroundColor: palette.eugeniaTint }}
            >
              <div className="flex items-center gap-3">
                <Sparkles size={24} className="text-[#b1466b]" />
                <div>
                  <h2 className="text-2xl font-semibold text-sb-black">Coming soon</h2>
                  <p className="text-sm text-sb-dark-grey">
                    Methodology locked, packaging in progress. Contact the owner to pilot.
                  </p>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {groupedServices.comingSoon.map((service) => {
                  const contact = contactDirectory[service.contactPerson];
                  return (
                    <div key={service.id} className="rounded-2xl border bg-white p-4" style={{ borderColor: palette.eugeniaTint }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-[#b1466b]">
                            Data Team Service
                          </p>
                          <h3 className="mt-1 text-lg font-semibold text-sb-black">
                            {service.title}
                          </h3>
                        </div>
                        <Badge className="rounded-full border border-[#FCD7E6] bg-[#FEF0F6] text-[#b1466b]">
                          Coming soon
                        </Badge>
                      </div>
                      <p className="mt-2 text-sm text-sb-dark-grey">{service.description}</p>
                      <p className="mt-3 text-xs uppercase tracking-wide text-gray-500">
                        Contact {contact.name} for pilot access
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2">
              <h2 className="text-2xl font-semibold text-sb-black">Need something else?</h2>
              <p className="text-sm text-sb-dark-grey">
                Email <a href="mailto:giovanni.antoniazzi@studiobereikbaar.nl" className="text-[#0F6B7C] underline">giovanni.antoniazzi@studiobereikbaar.nl</a>{' '}
                with project goals, timelines, and datasets. We will suggest the closest service or scope a new one.
              </p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
